import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
import React, { useState, useRef, useEffect } from 'react';
import styled from '@emotion/styled';
const ImageContainer = styled.div`
  position: relative;
  display: inline-block;
  overflow: hidden;
  background-color: var(--theme-paper-hover, #f9fafb);
  border-radius: 0.5rem;
  
  ${(props)=>props.aspectRatio && `
    aspect-ratio: ${props.aspectRatio};
  `}

  .dark & {
    background-color: var(--theme-paper-hover, #374151);
  }
`;
const Image = styled.img`
  width: 100%;
  height: 100%;
  object-fit: cover;
  transition: opacity 0.3s ease-in-out;
  opacity: ${(props)=>props.loaded ? 1 : 0};
`;
const Placeholder = styled.div`
  position: absolute;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  display: flex;
  align-items: center;
  justify-content: center;
  background-color: var(--theme-paper-hover, #f9fafb);
  opacity: ${(props)=>props.loaded ? 0 : 1};
  transition: opacity 0.3s ease-in-out;
  pointer-events: none;

  .dark & {
    background-color: var(--theme-paper-hover, #374151);
  }
`;
const LoadingSpinner = styled.div`
  width: 1.5rem;
  height: 1.5rem;
  border: 2px solid var(--theme-border, #e5e7eb);
  border-top: 2px solid var(--theme-primary, #10b981);
  border-radius: 50%;
  animation: spin 1s linear infinite;

  .dark & {
    border-color: var(--theme-border, #374151);
    border-top-color: var(--theme-primary, #10b981);
  }

  @keyframes spin {
    0% { transform: rotate(0deg); }
    100% { transform: rotate(360deg); }
  }
`;
const ErrorPlaceholder = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  gap: 0.5rem;
  color: var(--theme-text-secondary, #6b7280);
  font-size: 0.875rem;
  text-align: center;
  padding: 1rem;

  .dark & {
    color: var(--theme-text-secondary, #9ca3af);
  }
`;
const LazyImage = ({ src, alt, className, style, aspectRatio, placeholder, errorPlaceholder, onLoad, onError, onClick, rootMargin = '50px', threshold = 0.1 })=>{
    const [loaded, setLoaded] = useState(false);
    const [error, setError] = useState(false);
    const [inView, setInView] = useState(false);
    const imgRef = useRef(null);
    const containerRef = useRef(null);
    // Intersection Observer 설정
    useEffect(()=>{
        const observer = new IntersectionObserver(([entry])=>{
            if (entry.isIntersecting) {
                setInView(true);
                observer.disconnect();
            }
        }, {
            rootMargin,
            threshold
        });
        if (containerRef.current) {
            observer.observe(containerRef.current);
        }
        return ()=>observer.disconnect();
    }, [
        rootMargin,
        threshold
    ]);
    // 이미지 로드 핸들러
    const handleImageLoad = ()=>{
        setLoaded(true);
        if (onLoad) {
            onLoad();
        }
    };
    // 이미지 에러 핸들러
    const handleImageError = ()=>{
        setError(true);
        if (onError) {
            onError();
        }
    };
    // 기본 에러 플레이스홀더
    const defaultErrorPlaceholder = /*#__PURE__*/ _jsxs(ErrorPlaceholder, {
        children: [
            /*#__PURE__*/ _jsx("div", {
                style: {
                    fontSize: '1.5rem'
                },
                children: "🖼️"
            }),
            /*#__PURE__*/ _jsx("div", {
                children: "이미지를 불러올 수 없습니다"
            })
        ]
    });
    // 기본 로딩 플레이스홀더
    const defaultPlaceholder = /*#__PURE__*/ _jsx(LoadingSpinner, {});
    return /*#__PURE__*/ _jsxs(ImageContainer, {
        ref: containerRef,
        className: className,
        style: style,
        aspectRatio: aspectRatio,
        onClick: onClick,
        children: [
            inView && !error && /*#__PURE__*/ _jsx(Image, {
                ref: imgRef,
                src: src,
                alt: alt,
                loaded: loaded,
                onLoad: handleImageLoad,
                onError: handleImageError
            }),
            /*#__PURE__*/ _jsx(Placeholder, {
                loaded: loaded && !error,
                children: error ? errorPlaceholder || defaultErrorPlaceholder : placeholder || defaultPlaceholder
            })
        ]
    });
};
export default LazyImage;
